import { IAllGroupModel } from 'domains/designers/RuleDesigner/models/AllGroupModel';
import {
  RuleDesignerSurface,
  RuleServiceStepNotification,
  RuleStepType,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import {
  addTruthTableRowWithEvents,
  setTruthTableEditableValue,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/RuleEditService';
import {
  addRowBeforeWithEvents as addRowBeforeApi,
  addRowAfterWithEvents as addRowAfterApi,
  addColumnWithEvents as addColumnApi,
  startEditingStepValue,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import { RuleExecutionResult } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/Events';
import { SetValueEventObj } from './cells/OutputCell';
import { setTruthTableSpecifiedResultValue } from '@decisions/server/DecisionsFramework/Design/Flow/Service/TruthTable/TruthTableService';
import { newTruthTableSpecifiedResult } from '@decisions/server/DecisionsFramework/Design/Flow/Service/TruthTable/TruthTableSpecifiedResult';
import { IAnyGroupModel } from 'domains/designers/RuleDesigner/models/AnyGroupModel';
import { IRuleStepModel, RuleStepModel } from 'domains/designers/RuleDesigner/models/RuleStepModel';
import { IntersectionEditorInfo } from 'components/inputs/InlineEditor/InlineEditor';
import { showSequentialPicker } from 'utils/SequentialPickerUtils';
import { ActionModel } from '../RuleDesigner/models/ActionModel';
import { RuleActionTypeInfo } from '@decisions/server/DecisionsFramework/Design/Flow/RuleAction/RuleActionTypeInfo';
import { IActionLinkItems } from 'components/ActionLinks/ActionLinkModels';
import { groupBy } from 'utils/ArrayUtils';
import {
  isRuleTableHeaderDisplayData,
  RuleTableHeaderDisplayData,
} from '@decisions/server/DecisionsFramework/Design/Flow/RuleTable/RuleTableHeaderDisplayData';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { RULE_OUTPUT_ERROR_VALIDATION_CODE } from '../RuleDesigner/ValidationUtils';
import { IRootGroupModel } from '../RuleDesigner/models/RootGroupModel';

export type HandleNotificationsCallback = (notifications: RuleServiceStepNotification[]) => void;

export function canRowBeDeleted(rowModel: IAllGroupModel): boolean {
  return (rowModel?.ParentGroup?.getChildren() || []).length > 1;
}

export function canRowBeMovedDown(rowModel: IAllGroupModel): boolean {
  const children = rowModel?.ParentGroup?.getChildren() ?? [];
  return children.length > 0 && rowModel !== children[children.length - 1];
}

export function canRowBeMovedUp(rowModel: IAllGroupModel): boolean {
  const children = rowModel?.ParentGroup?.getChildren() ?? [];
  return children.length > 0 && rowModel !== children[0];
}

export function getRows(rootModel: IAllGroupModel): IAllGroupModel[] {
  const alls: IAllGroupModel[] = [];
  if (rootModel) {
    const anyGroup: IAnyGroupModel = rootModel.GetEitherGroup();
    if (anyGroup) {
      anyGroup.getChildren().forEach(m => {
        if (m.DisplayData.StepType === RuleStepType.AllGroup) {
          alls.push(m as IAllGroupModel);
        }
      });
    }
  }
  return alls;
}

export function getHeaders(rootModel: IRootGroupModel): RuleTableHeaderDisplayData[] {
  if (rootModel.ParentGroup != null) {
    throw new Error('Root step is expected');
  }

  return rootModel.RuleDisplayData.TableHeaders;
}

export function getHeaderSteps(rootModel: IAllGroupModel): IRuleStepModel[] {
  if (rootModel.ParentGroup != null) {
    throw new Error('Root step is expected');
  }

  return getRows(rootModel)[0].GetRuleStepModels();
}

export function getHeaderActions(rowModel: IAllGroupModel): ActionModel[] {
  return rowModel?.ActionsModel?.HeaderActions ?? [];
}

export function hasActionsColumn(rootModel: IRootGroupModel, surface: RuleDesignerSurface) {
  return rootModel.allowActions && surface !== RuleDesignerSurface.ExternalTruthTable;
}

export function getColumnCells(rootModel: IAllGroupModel, columnIndex: number): IRuleStepModel[] {
  const rows = getRows(rootModel);

  const column: IRuleStepModel[] = [];

  if (rows) {
    rows.forEach(model => {
      const children: IRuleStepModel[] = model.GetRuleStepModels();
      if (children && columnIndex < children.length) {
        const step: IRuleStepModel = children[columnIndex];

        if (step != null) {
          column.push(step);
        }
      }
    });
  }

  return column;
}

/**
 * Return ids of cells for specified column
 *
 * @param columnIndex Number from 0 to columnNumber - 1
 */
export function getColumnCellsIds(rootModel: IAllGroupModel, columnIndex: number): string[] {
  return getColumnCells(rootModel, columnIndex).map(item => item.Id);
}

export function getTableColumnsCount(rootModel: IRootGroupModel, surface: RuleDesignerSurface) {
  if (rootModel.ParentGroup != null) {
    throw new Error('Root step is expected');
  }

  //TODO alex: maybe return object with all these properties?
  const designerColumns = rootModel.IsInDebugMode() ? 0 : 2; //add column, add output
  const executionColumns = surface !== RuleDesignerSurface.ExternalTruthTable ? 1 : 0;
  const verbColumns = getHeaders(rootModel).length;
  const outputColumns = rootModel.RuleDisplayData.DefaultResults.length;
  const actionColumns = getActionColumnsCount(rootModel, surface);

  const columns = executionColumns + verbColumns + outputColumns + actionColumns + designerColumns;

  return columns;
}

export function getActionColumnsCount(rootModel: IRootGroupModel, surface: RuleDesignerSurface) {
  if (rootModel.ParentGroup != null) {
    throw new Error('Root step is expected');
  }

  const actionColumns = hasActionsColumn(rootModel, surface) ? 1 : 0;
  const headerActionsColumns = getHeaderActions(rootModel).length;

  return actionColumns + headerActionsColumns;
}

export function noFilterResults(isFilterApplied: boolean, rows: IAllGroupModel[]) {
  return isFilterApplied && rows.length === 1 && rows[0].Id === 'EMPTY_ROW';
}

export function addColumn(rootModel: IAllGroupModel, handleNotifications: HandleNotificationsCallback) {
  addColumnApi({
    ruleSessionId: rootModel.RuleSessionId,
  }).then(data => {
    const notifications = data.map(item => item.OutputValue as RuleServiceStepNotification);
    handleNotifications(notifications);
  });
}

export function addRow(model: IAllGroupModel, handleNotifications: HandleNotificationsCallback) {
  if (model.ParentGroup != null) {
    throw new Error('Expected root step');
  }

  addTruthTableRowWithEvents({
    ruleSessionId: model.RuleSessionId,
    rootGroupId: model.Id,
  }).then(data => {
    const notifications = data.map(item => item.OutputValue as RuleServiceStepNotification);

    handleNotifications(notifications);
  });
}

export async function addRowBefore(
  sessionId: string,
  beforeRowId: string,
  handleNotifications: HandleNotificationsCallback
) {
  const response = await addRowBeforeApi({
    ruleSessionId: sessionId,
    beforeRowId,
  });

  const notifications = response.map(item => item.OutputValue as RuleServiceStepNotification);
  handleNotifications(notifications);
}

export async function addRowAfter(
  sessionId: string,
  afterRowId: string,
  handleNotifications: HandleNotificationsCallback
) {
  const data = await addRowAfterApi({
    ruleSessionId: sessionId,
    afterRowId,
  });

  const notifications = data.map(item => item.OutputValue as RuleServiceStepNotification);
  handleNotifications(notifications);
}

export async function rowOutputSetValue({ output, value, model }: SetValueEventObj) {
  if (output.Value !== value) {
    output.Value = value;
    await setTruthTableSpecifiedResultValue({
      ruleSessionId: model.RuleSessionId,
      specResult: newTruthTableSpecifiedResult({
        RuleStepId: model.Id,
        Name: output.DataDescriptionName,
        Value: value,
      }),
    });
  }
}

export function getStepIntersectionInfo(ruleStep: IRuleStepModel): IntersectionEditorInfo {
  return {
    mappingType: ruleStep.DisplayData.EditableMappingType,
    dataType: ruleStep.DisplayData.EditableDataType,
    displayValue: ruleStep.DisplayData.EditableDataValue,
    canNavigate: true,
    commands: {
      setIntersectionStringValue: async (intersection: IntersectionEditorInfo, value: string) => {
        if (ruleStep) {
          await setTruthTableEditableValue({
            cellValue: value,
            ruleSessionId: ruleStep.RuleSessionId,
            ruleStepId: ruleStep.Id,
          });
        }
      },
      showFullEditor: (intersection: IntersectionEditorInfo) => {
        if (intersection.mappingType === 'SelectValueInputMapping') {
          startEditingStepValue({
            stepId: ruleStep.Id,
            truthTableSessionId: ruleStep.RuleSessionId,
          }).then(showSequentialPicker);
        }
      },
    },
  };
}

export function getResultBackgroundColor(result: RuleExecutionResult | null) {
  if (result === RuleExecutionResult.Success) {
    return 'rgb(230, 255, 230)';
  }
  if (result === RuleExecutionResult.Failure) {
    return 'rgb(254, 235, 235)';
  } else {
    return '';
  }
}

export function getRowExecutionResult(
  row: IAllGroupModel,
  isDebug: boolean,
  isSampling: boolean
): RuleExecutionResult | null {
  //greedy evaluation -
  const childModels = row.getChildren();
  if (!childModels) {
    return null;
  } else if (childModels.length === 0 && (isDebug || isSampling)) {
    return RuleExecutionResult.Success;
    //if all children has no debug model or not evaluated, then - null
  } else if (
    childModels.every((model, index, arr) => {
      return !model.Debug || !model.Debug.evaluation;
    })
  ) {
    return null;
    //if all Success or not evaluated, then - Success
  } else if (
    childModels.every((model, index, arr) => {
      return model.Debug && (model.Debug.isSuccess || !model.Debug.evaluation);
    })
  ) {
    return RuleExecutionResult.Success;
  } else {
    return RuleExecutionResult.Failure;
  }
}

export function getHeaderActionsMenu(actions: RuleActionTypeInfo[]): IActionLinkItems {
  const linkItems: IActionLinkItems = { current: [], nested: new Map() };
  const headerActions = actions.filter(item => item.IsChangeAction);
  const groups = groupBy(headerActions, item => item.Category);

  for (const groupName in groups) {
    const group = groups[groupName];

    if (groupName === 'null') {
      linkItems.current = group.map(item => ({
        label: item.Title,
        value: item.TypeName,
      }));
    } else {
      linkItems.nested!.set(groupName, {
        current: group.map(item => ({
          label: item.Title,
          value: item.TypeName,
        })),
      });
    }
  }

  return linkItems;
}

export function getRowSpan(rootModel: IRootGroupModel, stepId: string): number {
  return (
    ((rootModel.RuleDisplayData.CellSettings ?? []).find(item => item.Name === stepId)?.OutputValue as number) ?? 1
  );
}

export function hasMergedCells(rootModel: IRootGroupModel): boolean {
  return rootModel.RuleDisplayData.CellSettings?.length > 0;
}

export function getOutputIssues(issues: ValidationIssue[], outputName: string): ValidationIssue[] {
  return (issues || []).filter(
    issue =>
      issue.ValidationCode === RULE_OUTPUT_ERROR_VALIDATION_CODE &&
      //TODO alex: need to send correct output name from server,
      // we use `Specified` prefix to store data description and mapping inside input
      (issue.ReferenceProperty === outputName ||
        issue.ReferenceProperty === `Specified ${outputName}` ||
        `Specified ${issue.ReferenceProperty}` === outputName)
  );
}

export function getPhraseColumnName(header: RuleTableHeaderDisplayData | IRuleStepModel) {
  if (!header) {
    return '';
  }

  if ('__type' in header && isRuleTableHeaderDisplayData(header)) {
    return `${header.EvaluateWithNot ? 'not' : ''} ${header.AnchorInfo ?? NoAnchorText} ${header.VerbInfo ??
      NoVerbText}`.trim();
  }

  const step = header as IRuleStepModel;

  return `${step.EvaluateWithNot ? 'not' : ''} ${step.TokenAnchorInfo ?? NoAnchorText} ${step.TokenVerbInfo ??
    NoVerbText}`.trim();
}

export function isStepForHeader(step: IRuleStepModel, header: RuleTableHeaderDisplayData): boolean {
  return step.DisplayData.ColumnId === header.Id;
}

export function getStepsForHeader(row: IAllGroupModel, header: RuleTableHeaderDisplayData): IRuleStepModel[] {
  return (row.getChildren() || []).filter(item => isStepForHeader(item as RuleStepModel, header)) as IRuleStepModel[];
}

export const NoAnchorText = '[Select Data Element]';
export const NoVerbText = '[Pick Verb]';

export const TruthTableUtils = { getRowSpan };
